import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Multiselect } from '@/components/ui/multiselect';
import { ChevronDown } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useBackOfficeUsersService,
  useCreateWorkspaceService,
} from '@/services/backoffice';
import { cn } from '@/utils/classUtils';
import { useMemo, useState, type FC } from 'react';
import { toast } from 'sonner';

interface CreateWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateWorkspaceModal: FC<CreateWorkspaceModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { createWorkspace, isLoading } = useCreateWorkspaceService();
  const { users } = useBackOfficeUsersService();

  const [newWorkspace, setNewWorkspace] = useState({
    name: '',
    uname: '',
    userIds: [] as string[],
  });

  // Transform users for multiselect
  const userOptions = useMemo(
    () =>
      users.map(user => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName} (${user.email})`,
      })),
    [users]
  );

  const formValidation = useMemo(() => {
    const isNameValid = newWorkspace.name.trim() !== '';
    const isUnameValid =
      newWorkspace.uname.trim() !== '' &&
      /^[a-z0-9-]+$/.test(newWorkspace.uname);

    const isFormValid = isNameValid && isUnameValid;

    const invalidFields = [];
    if (!isNameValid) invalidFields.push('Workspace name is required');
    if (!isUnameValid) {
      if (newWorkspace.uname.trim() === '') {
        invalidFields.push('Workspace URL is required');
      } else {
        invalidFields.push(
          'Workspace URL must contain only lowercase letters, numbers, and hyphens'
        );
      }
    }

    return {
      isValid: isFormValid,
      errors: invalidFields,
    };
  }, [newWorkspace]);

  const handleCreateWorkspace = () => {
    createWorkspace(newWorkspace, {
      onSuccess: () => {
        toast.success('Workspace created successfully');
        onOpenChange(false);
        setNewWorkspace({
          name: '',
          uname: '',
          userIds: [],
        });
      },
      onError: (error: any) => {
        const errorMessage =
          error?.message ||
          error?.response?.body?.message ||
          error?.response?.data?.message ||
          'Failed to create workspace';

        toast.error(errorMessage);
      },
    });
  };

  const handleUnameChange = (value: string) => {
    // Auto-convert to lowercase and replace invalid characters
    const cleanUname = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setNewWorkspace({ ...newWorkspace, uname: cleanUname });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Workspace Name</Label>
              <Input
                id="workspace-name"
                value={newWorkspace.name}
                onChange={e =>
                  setNewWorkspace({ ...newWorkspace, name: e.target.value })
                }
                placeholder="Enter workspace name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="workspace-uname">Workspace URL</Label>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  /workspace/
                </span>
                <Input
                  id="workspace-uname"
                  value={newWorkspace.uname}
                  onChange={e => handleUnameChange(e.target.value)}
                  placeholder="workspace-url"
                  className="font-mono text-sm"
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Only lowercase letters, numbers, and hyphens allowed
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="workspace-users">Team Members (Optional)</Label>
              <Multiselect
                options={userOptions}
                selected={newWorkspace.userIds}
                onChange={selected =>
                  setNewWorkspace({ ...newWorkspace, userIds: selected })
                }
                showSearch
                placeholder="Select team members..."
                className="w-full"
                contentClassName="max-h-[200px] overflow-y-auto w-full"
                trigger={
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn(
                      'justify-between font-normal w-full',
                      newWorkspace.userIds.length === 0 &&
                        'text-muted-foreground'
                    )}
                  >
                    <span className="truncate">
                      {newWorkspace.userIds.length === 0
                        ? 'Select team members...'
                        : newWorkspace.userIds.length === 1
                          ? userOptions.find(
                              opt => opt.value === newWorkspace.userIds[0]
                            )?.label || newWorkspace.userIds[0]
                          : `${newWorkspace.userIds.length} items selected`}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                }
              />
              <p className="text-xs text-muted-foreground">
                {newWorkspace.userIds.length === 0
                  ? 'No team members selected'
                  : newWorkspace.userIds.length === 1
                    ? `${newWorkspace.userIds.length} team member selected`
                    : `${newWorkspace.userIds.length} team members selected`}
              </p>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      onClick={handleCreateWorkspace}
                      disabled={!formValidation.isValid || isLoading}
                    >
                      Create Workspace
                    </Button>
                  </div>
                </TooltipTrigger>
                {!formValidation.isValid && (
                  <TooltipContent>
                    <div className="space-y-1">
                      {formValidation.errors.map((error, index) => (
                        <div key={index} className="text-xs">
                          • {error}
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
